import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAutoSyncContext } from '../hooks/useAutoSync'

// This page lives at /auth/schwab/callback — the redirect_uri registered with
// Schwab Developer. After Schwab OAuth, the user lands here with ?code=XXX.
//
// The route is NOT wrapped in ProtectedRoute because the Supabase session may
// have expired during the 7-day Schwab token window. If the user is NOT
// authenticated when the callback fires, we save the authorization code to
// sessionStorage, redirect to /auth for login, and then resume the token
// exchange after they log in.

const SESSION_KEY_CODE = 'schwab_oauth_code'

export default function SchwabCallback() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { refreshTokenWarning } = useAutoSyncContext()
  const called = useRef(false)
  const [status, setStatus] = useState('Connecting Schwab…')

  useEffect(() => {
    if (called.current) return
    called.current = true

    const code = searchParams.get('code')
    const savedCode = sessionStorage.getItem(SESSION_KEY_CODE)
    const error = searchParams.get('error') || searchParams.get('error_code')
    const errorDesc = searchParams.get('error_description') || searchParams.get('error_message')

    if (error) {
      const msg = errorDesc || error || 'Authorization denied'
      navigate(`/integrations?error=${encodeURIComponent(msg)}`, { replace: true })
      return
    }

    const effectiveCode = code || savedCode
    if (!effectiveCode) {
      navigate('/integrations?error=No+authorization+code', { replace: true })
      return
    }

    ;(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()

        if (!session) {
          // No active session — save the code so we can resume after login.
          sessionStorage.setItem(SESSION_KEY_CODE, effectiveCode)
          setStatus('Session expired — redirecting to login…')
          navigate('/auth', { replace: true })
          return
        }

        // Session exists — clear any saved code and proceed with exchange.
        sessionStorage.removeItem(SESSION_KEY_CODE)

        setStatus('Exchanging authorization code…')
        const params = new URLSearchParams({ action: 'callback', code: effectiveCode })
        const res = await fetch(`/api/schwab/auth?${params}`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        })
        const json = await res.json()

        if (!res.ok) {
          navigate(`/integrations?error=${encodeURIComponent(json.error || 'Connection failed')}`, { replace: true })
          return
        }

        // The provider mounts before this exchange and may still hold the old
        // expiry date. Refresh it after the server confirms the new tokens are
        // saved, without waiting for (or launching) a full account sync.
        await refreshTokenWarning(session.user)
        navigate('/integrations?connected=true', { replace: true })
      } catch (err) {
        navigate(`/integrations?error=${encodeURIComponent(err.message)}`, { replace: true })
      }
    })()
  }, [])

  return (
    <div className="min-h-screen bg-parchment flex items-center justify-center">
      <div className="font-mono text-xs tracking-widest text-inkMuted uppercase animate-pulse">
        {status}
      </div>
    </div>
  )
}
